import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import Header from '../components/Headers/Header';
import CustomLoader from '../components/Loader/CustomLoader';
import { useQuery, gql, useApolloClient } from '@apollo/client';
import DataTable from 'react-data-table-component';
import orderBy from 'lodash/orderBy';
import { customStyles } from '../utils/manageLocalUsersTable';
import useGlobalStyles from '../utils/globalStyles';
import TableHeader from '../components/TableHeader';
import AddSystemConfigForm from '../components/AddSystemConfigForm/AddSystemConfigForm';

import {
  Container,
  Box,
  Typography,
  Button,
} from '@mui/material';
import { Icon } from '@iconify/react';


// === GraphQL Query ===
export const GET_SYSTEM_CONFIGS = gql`
  query GetSystemConfigs {
    getSystemConfigs {
      _id
      name
      value
      description
      contents
      isActive
    }
  }
`;

const ManageSystemConfigs = (props) => {
  const { t } = props;
  const client = useApolloClient();
  const globalClasses = useGlobalStyles();

  const [addConfigModal, setAddConfigModal] = useState(false);

  const openAddConfigModal = () => {
    setAddConfigModal(true);
  };

  const closeAddConfigModal = () => {
    setAddConfigModal(false);
  };

  const { data, error: errorQuery, loading: loadingQuery, refetch: refetchSystemConfigs } = useQuery(
    GET_SYSTEM_CONFIGS,
    {
      onError: (error) => {
        const isForbiddenError =
          error && error.graphQLErrors.some((err) => err.extensions.code === 'FORBIDDEN');

        if (isForbiddenError) {
          localStorage.removeItem('user-native');
          client.clearStore();
          props.history.push('/auth/login');
        }
        console.error("Error fetching system configs:", error);
      },
    }
  );

  const columns = [
    {
      name: t('Sl No.'),
      cell: (row, index) => <>{index + 1}</>,
      grow: 0,
      width: '60px',
    },
    {
      name: t('Name'),
      sortable: true,
      selector: (row) => row.name,
      // Removed minWidth from here, moving it to style for consistency
      // MinWidth is applied via style below to allow wrapping
      cell: (row) => (
        <Typography variant="body2" sx={{
            // Removed nowrap, overflow, textOverflow to allow wrapping
            // Added maxWidth to allow wrapping within a certain column width
            maxWidth: '200px', // Example width, adjust as needed
            // Added for consistency with orderColumns
            textAlign: 'left',
          }}>
          {row.name}
        </Typography>
      ),
      style: { minWidth: "150px", cursor: 'pointer', textAlign: 'left' }, // Added minWidth and textAlign
    },
    {
      name: t('Value'),
      sortable: true,
      selector: (row) => row.value,
      cell: (row) => (
        <Typography variant="body2" sx={{
            // Allow wrapping
            maxWidth: '250px', // Example width, adjust as needed
            textAlign: 'left',
          }}>
          {row.value}
        </Typography>
      ),
      style: { minWidth: "200px", textAlign: 'left' }, // Added minWidth and textAlign
    },
    {
      name: t('Description'),
      sortable: true,
      selector: (row) => row.description,
      cell: (row) => (
        <Typography variant="body2" sx={{
            // Allow wrapping
            maxWidth: '300px', // Example width, adjust as needed
            textAlign: 'left',
          }}>
          {row.description}
        </Typography>
      ),
      style: { minWidth: "350px", cursor: 'pointer', textAlign: 'left' }, // Added minWidth and textAlign
    },
{
  name: t('Contents'),
  cell: (row) => {
    const content = row.contents;

    const isContentEmpty =
      content === null || content === '' || content === 'null';

    return (
      <Typography variant="body2" color={isContentEmpty ? 'textSecondary' : 'textPrimary'}>
        {isContentEmpty ? t('N/A') : content}
      </Typography>
    );
  },
  grow: 1,
  minWidth: '100px',
  style: { textAlign: 'center' },
},
    {
      name: t('Active'),
      sortable: true,
      selector: (row) => row.isActive,
      cell: (row) => (
        <Icon
          icon={row.isActive ? 'mdi:check-circle' : 'mdi:close-circle'}
          color={row.isActive ? 'green' : 'red'}
          width="24"
          height="24"
        />
      ),
      minWidth: '80px',
      center: true,
    },
  ];

  const customSort = (rows, field, direction) => {
    const handleField = (row) => {
      const value = row[field];
      return value != null ? String(value).toLowerCase() : '';
    };
    return orderBy(rows, handleField, direction);
  };

  const handleSort = (column, sortDirection) => {
    console.log(`Client-side sorting by: ${column.selector} in ${sortDirection} direction`);
  };

  const systemConfigs = (data && data.getSystemConfigs) ? data.getSystemConfigs : [];

  return (
    <>
      <Header />

      <Container className={globalClasses.flex} fluid="true">
        <Box sx={{ textAlign: 'right', mb: 3 }}>
          <Button
            className={globalClasses.dashbordBtn}
            onClick={openAddConfigModal}
          >
            <Icon icon="ic:twotone-plus" style={{ color: 'white' }} />{' '}
            {t('Add New System Config')}
          </Button>
        </Box>

        {errorQuery ? (
          <Box mt={3} color="error.main">
            <Typography variant="h6">
              {t('Error')}! {errorQuery.message}
            </Typography>
          </Box>
        ) : (
          <DataTable
            subHeader={true}
            title={<TableHeader title={t('Manage System Configurations')} />}
            columns={columns}
            data={systemConfigs}
            pagination
            progressPending={loadingQuery}
            progressComponent={<CustomLoader />}
            onSort={handleSort}
            sortFunction={customSort}
            customStyles={customStyles}
            paginationIconLastPage=""
            paginationIconFirstPage=""
          />
        )}
      </Container>

      {addConfigModal && (
        <AddSystemConfigForm
          onClose={closeAddConfigModal}
          refetch={refetchSystemConfigs}
        />
      )}
    </>
  );
};

export default withTranslation()(ManageSystemConfigs);
